import { normalizeYo } from './i18n';

export interface GradeResult {
    grade: 'exact' | 'accepted' | 'wrong';
    score: 100 | 70 | 0;
    matchedWord?: string;
    displayAnswer: string;
}

/**
 * 정답 문자열에서 개별 허용 답안을 추출:
 * - "/" 또는 ","로 분리
 * - 괄호 안 텍스트도 별도 변형으로 추출: "память (ностальгия)" → ["память", "ностальгия"]
 */
function splitAnswerVariants(answer: string): string[] {
    const variants: string[] = [];

    // 괄호 안 텍스트를 별도 변형으로 추출
    const parenRegex = /\(([^)]+)\)/g;
    let match: RegExpExecArray | null;
    while ((match = parenRegex.exec(answer)) !== null) {
        const inner = match[1].trim();
        if (inner) {
            // 괄호 안에 /,가 있으면 추가 분리
            const innerParts = inner.split(/\s*[/,]\s*/);
            for (const p of innerParts) {
                const trimmed = p.trim();
                if (trimmed && !variants.includes(trimmed)) variants.push(trimmed);
            }
        }
    }

    // 괄호 제거 후 메인 답안 분리
    const noParen = answer.replace(/\s*\([^)]*\)\s*/g, ' ').replace(/\s+/g, ' ').trim();
    const parts = noParen.split(/\s*[/,]\s*/);
    for (const part of parts) {
        const trimmed = part.trim();
        if (trimmed && !variants.includes(trimmed)) variants.push(trimmed);
    }
    return variants;
}

/**
 * "цвет" 접미사 제거: "чёрный цвет" → "чёрный"
 */
function stripCvet(s: string): string {
    return s.replace(/\s+цвет$/i, '').trim();
}

/**
 * 3단계 채점: exact(100) / accepted(70) / wrong(0)
 * - ё/е 정규화 적용 (normalizeYo)
 * - 정답에 "/", "," 포함 시 각 부분을 개별 정답으로 인정
 * - 괄호 안 보충 설명 제거한 형태도 허용
 * - "цвет" 접미사 유무 관계없이 매칭 (색상 단어 편의)
 * - accepted는 허용 정답 리스트와 매칭
 */
export function gradeAnswer(
    userInput: string,
    correctAnswer: string,
    acceptedAnswers: string[]
): GradeResult {
    const input = normalizeYo(userInput.trim().toLowerCase());
    const correct = normalizeYo(correctAnswer.trim().toLowerCase());

    // 1. exact match (전체 문자열)
    if (input === correct) {
        return { grade: 'exact', score: 100, displayAnswer: correctAnswer };
    }

    // 1.5 "цвет" 접미사 유연 매칭
    if (stripCvet(input) === stripCvet(correct) && stripCvet(input).length > 0) {
        return { grade: 'exact', score: 100, displayAnswer: correctAnswer };
    }

    // 2. 정답의 개별 변형 매칭 (/, 쉼표 분리 + 괄호 제거)
    const variants = splitAnswerVariants(correctAnswer);
    for (const v of variants) {
        const nv = normalizeYo(v.toLowerCase());
        if (nv === input || stripCvet(nv) === stripCvet(input)) {
            return { grade: 'exact', score: 100, displayAnswer: correctAnswer };
        }
    }

    // 3. accepted match
    if (acceptedAnswers && acceptedAnswers.length > 0) {
        for (const acc of acceptedAnswers) {
            const na = normalizeYo(acc.trim().toLowerCase());
            if (na === input || stripCvet(na) === stripCvet(input)) {
                return {
                    grade: 'accepted',
                    score: 70,
                    matchedWord: acc,
                    displayAnswer: correctAnswer,
                };
            }
        }
    }

    // 4. wrong
    return { grade: 'wrong', score: 0, displayAnswer: correctAnswer };
}
